'use es6';

import { List, Map as ImmutableMap, OrderedMap } from 'immutable';
import { DEFAULT_DEBUG } from '../../constants/debug';
import { getLegacyDefaultColorOptions } from '../../highcharts/options/defaultOptions';
import { THEME } from 'reporting-data/tsTypes/visualization/colors';
import { applyTransformations } from '../../transforms/applyTransformations';
import { buildChartOptions } from './options/chartOptions';
import { mapLayerSeries, mapLayersToAxes, mapSeriesWithEvents, setLayerOrdering, setConfiguredLinkedSeries } from './xyLayers';
import { AxisEncoding, AxisTypes, Chart, ColorEncoding, DetailEncoding, Events, ExcludePointsFromTooltipScopes, MarkEncoding, MarkTypes, SeriesStyle, SizingEncoding, Style } from './xyRecords';
import { label, rateOfChange } from './xyUtils';
import debounce from 'react-utils/debounce';
import { applyOpacity } from './xyUtils';
import { setSeriesNameForInsightLayers } from './xyLayers';
import { getLayerLinkedToParts } from './xyLayers';
import { DATASET_INSIGHT_TYPES } from 'reporting-data/tsTypes/datasetInsightTypes';
import { findLinkedLayer } from './xyLayers';
import { isXCategorical } from './xyUtils';
const makeColorPicker = colors => {
  let index = 0;
  return () => {
    const color = colors.get(index);
    index = (index + 1) % colors.count();
    return color;
  };
};
const makePatternPicker = patterns => {
  let index = 0;
  return () => {
    const pattern = patterns.get(index);
    index = (index + 1) % patterns.count();
    return pattern;
  };
};
const invertAxes = ({
  xAxis,
  yAxis
}, orient) => {
  const normal = {
    xAxis,
    yAxis
  };
  const inverted = {
    inverted: true,
    xAxis: yAxis,
    yAxis: xAxis
  };
  const xHidden = xAxis.some(({
    type
  }) => type === AxisTypes.hidden || type === AxisTypes.scaffold);
  const yHidden = yAxis.some(({
    type
  }) => type === AxisTypes.hidden || type === AxisTypes.scaffold);
  const xCategorical = xAxis.some(({
    type
  }) => type === AxisTypes.category || type === AxisTypes.datetime);
  const yCategorical = yAxis.some(({
    type
  }) => type === AxisTypes.category || type === AxisTypes.datetime);
  if (xHidden) {
    return yCategorical ? inverted : normal;
  }
  if (yHidden) {
    return xCategorical ? normal : inverted;
  }
  if (xCategorical && yCategorical) {
    return normal;
  }
  if (yCategorical) {
    return inverted;
  }
  if (!xCategorical && !yCategorical && orient === 'horizontal') {
    return inverted;
  }
  return normal;
};
const isForecastInsightLayer = layer => {
  return layer.get('insightType') === DATASET_INSIGHT_TYPES.FORECAST_TRENDLINE;
};
const findPrimaryLinkedLayerForForecastInsight = (currentLayer, allLayers, depth = 1 // don't expect callers to pass this parameter
) => {
  // Protection against infinite recursion. We should never need to a recurse
  // more than the total number of layers.
  if (depth > allLayers.length) {
    return undefined;
  }
  const linkedLayer = findLinkedLayer(currentLayer, allLayers);
  const linkedLayerLinkedToParts = linkedLayer ? getLayerLinkedToParts(linkedLayer) : undefined;
  if (linkedLayerLinkedToParts) {
    return findPrimaryLinkedLayerForForecastInsight(linkedLayer, allLayers, depth + 1);
  }
  return linkedLayer;
};
const alignLayerDataForForecastInsights = allLayers => {
  const layerIdsToIndexMap = {};
  const layersWithForecastInsightsMap = {};

  // build a map of layer ids to their index in the invertedLayers list
  // and a map of primary layers with their related forecast layers
  allLayers.forEach((layer, index) => {
    const layerId = layer.get('id');
    layerIdsToIndexMap[layerId] = index;
    if (isForecastInsightLayer(layer)) {
      const primaryLayer = findPrimaryLinkedLayerForForecastInsight(layer, allLayers);
      const primaryLayerId = primaryLayer.get('id');
      if (!(primaryLayerId in layersWithForecastInsightsMap)) {
        layersWithForecastInsightsMap[primaryLayerId] = {
          layer: primaryLayer,
          forecastInsightLayers: []
        };
      }
      layersWithForecastInsightsMap[primaryLayerId].forecastInsightLayers.push({
        layer,
        linkedToParts: getLayerLinkedToParts(layer)
      });
    }
  });
  let layersWithAlignedData = allLayers;

  // prepend primary layer data points to the forecast layer data
  // and append forecast layer data points to the primary layer data
  allLayers.forEach(layer => {
    const layerId = layer.get('id');
    const xIsCategorical = isXCategorical(layer);
    const categoryAxis = xIsCategorical ? 'x' : 'y';
    const valueAxis = xIsCategorical ? 'y' : 'x';
    const categoryFieldPath = [categoryAxis, 'field'];
    const valueFieldPath = [valueAxis, 'field'];

    // if the layer is in the layersWithForecastInsightsMap, then it is a primary layer
    // with related forecast layers
    if (layerId in layersWithForecastInsightsMap) {
      const primaryLayer = layersWithForecastInsightsMap[layerId].layer;
      const primaryLayerIndex = layerIdsToIndexMap[primaryLayer.get('id')];
      const primaryValueField = primaryLayer.getIn(valueFieldPath);
      const forecastInsightLayers = layersWithForecastInsightsMap[layerId].forecastInsightLayers;
      const allPrimaryLayerData = primaryLayer.getIn(['dataset', 'data']);
      const primaryDataLastNonEmptyValueIndex = allPrimaryLayerData.findLastIndex(dataItem => dataItem.get(primaryValueField) !== null);
      const primaryDataHasTrailingEmptyValues = primaryDataLastNonEmptyValueIndex !== allPrimaryLayerData.size - 1;
      const primaryLayerData = primaryDataHasTrailingEmptyValues ? allPrimaryLayerData.slice(0, primaryDataLastNonEmptyValueIndex + 1) : allPrimaryLayerData;
      const primaryLayerDataProperties = primaryLayer.getIn(['dataset', 'properties']);

      // if forecast layers are present, prepend primary layer data to forecast layer data
      if (forecastInsightLayers.length) {
        const primaryColorField = primaryLayer.getIn(['color', 'field']);
        const primaryLayerCategoryField = primaryLayer.getIn(categoryFieldPath);
        const primaryLayerCategoryPropertyReferences = primaryLayerDataProperties.getIn([primaryLayerCategoryField, 'references']);
        const primaryLayerDefinedOrder = primaryLayer.getIn([categoryAxis, 'order', 'defined']);
        let combinedForecastLayerDataMap = new OrderedMap();
        let combinedForecastLayerCategoryPropertyReferences = new ImmutableMap();
        forecastInsightLayers.forEach(forecastLayer => {
          const forecastLayerIndex = layerIdsToIndexMap[forecastLayer.layer.get('id')];
          const forecastLayerLinkedToParts = forecastLayer.linkedToParts;
          const filteredPrimaryLayerData = forecastLayerLinkedToParts.breakdownValue ? primaryLayerData.filter(primaryLayerDataItem => primaryLayerDataItem.get(primaryColorField) === forecastLayerLinkedToParts.breakdownValue) : primaryLayerData;
          const forecastInsightLayerData = forecastLayer.layer.getIn(['dataset', 'data']);
          if (forecastInsightLayerData.count() > 0 && filteredPrimaryLayerData.count() > 0) {
            const forecastInsightLayerCategoryField = forecastLayer.layer.getIn(categoryFieldPath);
            const forecastInsightLayerValueField = forecastLayer.layer.getIn(valueFieldPath);
            const dataToPrependToForecastInsightLayerData = filteredPrimaryLayerData.map((filteredPrimaryLayerDataItem, filteredPrimaryLayerDataIndex) => {
              const isLastPrimaryDataItem = filteredPrimaryLayerDataIndex === filteredPrimaryLayerData.count() - 1;

              // for the last primary data item, we need to copy the metric
              // values from the first forecast layer data to ensure the
              // series are connected in the chart
              if (isLastPrimaryDataItem) {
                const firstForecastInsightLayerDataItem = forecastInsightLayerData.first();
                const firstForecastInsightLayerDataItemValue = firstForecastInsightLayerDataItem.get(forecastInsightLayerValueField);
                const filteredPrimaryLayerDataItemValue = filteredPrimaryLayerDataItem.get(primaryValueField);
                const forecastInsightLayerValueIsList = List.isList(firstForecastInsightLayerDataItemValue);
                const connectingValue = forecastInsightLayerValueIsList ? List(Array(firstForecastInsightLayerDataItemValue.count()).fill(filteredPrimaryLayerDataItemValue)) : filteredPrimaryLayerDataItemValue;
                return filteredPrimaryLayerDataItem.set(forecastInsightLayerValueField, connectingValue);
              }

              // for all other primary data items, we set the metric values to null
              return filteredPrimaryLayerDataItem.set(primaryValueField, null).set(forecastInsightLayerValueField, null);
            });

            // prepend primary layer data to forecast layer data
            const prependedForecastInsighLayerData = forecastInsightLayerData.unshift(...dataToPrependToForecastInsightLayerData);
            const forecastLayerDefinedOrder = forecastLayer.layer.getIn([categoryAxis, 'order', 'defined']);
            layersWithAlignedData = layersWithAlignedData.withMutations(mutableLayersWithAlignedData => {
              const existingExcludePointsFromTooltip = layersWithAlignedData.getIn([forecastLayerIndex, 'excludePointsFromTooltip']);

              // if excludePointsFromTooltip is already configured, it should not be overridden
              if (!existingExcludePointsFromTooltip) {
                mutableLayersWithAlignedData.setIn([forecastLayerIndex, 'excludePointsFromTooltip'], {
                  scope: ExcludePointsFromTooltipScopes.range,
                  to: dataToPrependToForecastInsightLayerData.size - 1
                });
              }
              mutableLayersWithAlignedData.setIn([forecastLayerIndex, 'dataset', 'data'], prependedForecastInsighLayerData);
              if (primaryLayerDefinedOrder && forecastLayerDefinedOrder) {
                // prepend primary layer defined order to forecast layer defined order if applicable
                const prependedForecastInsighLayerDefinedOrder = forecastLayerDefinedOrder.unshift(...primaryLayerDefinedOrder);
                mutableLayersWithAlignedData.setIn([forecastLayerIndex, categoryAxis, 'order', 'defined'], prependedForecastInsighLayerDefinedOrder);
              }

              // merge primary layer category property references into forecast layer category property references
              mutableLayersWithAlignedData.mergeIn([forecastLayerIndex, 'dataset', 'properties', forecastInsightLayerCategoryField, 'references'], primaryLayerCategoryPropertyReferences);
            });
            forecastInsightLayerData.forEach(forecastInsightLayerDataItem => {
              const forecastInsightLayerCategoryValue = forecastInsightLayerDataItem.get(forecastInsightLayerCategoryField);
              const combinedForecastLayerDataKey = forecastLayerLinkedToParts.breakdownValue ? `${forecastLayerLinkedToParts.breakdownValue}-${forecastInsightLayerCategoryValue}` : forecastInsightLayerCategoryValue;

              // merge all forecast layer data into a single data list
              // to be appended to the primary layer data
              combinedForecastLayerDataMap = combinedForecastLayerDataMap.set(combinedForecastLayerDataKey, combinedForecastLayerDataMap.get(combinedForecastLayerDataKey, new ImmutableMap()).merge(forecastInsightLayerDataItem.set(primaryValueField, null).set(forecastInsightLayerValueField, null)));
              const forecastInsightLayerDataProperties = forecastLayer.layer.getIn(['dataset', 'properties']);
              const forecastInsightLayerCategoryPropertyReferences = forecastInsightLayerDataProperties.getIn([forecastInsightLayerCategoryField, 'references']);

              // merge all forecast layer category property references into a single map
              // to be merged with the primary layer category property references
              combinedForecastLayerCategoryPropertyReferences = combinedForecastLayerCategoryPropertyReferences.merge(forecastInsightLayerCategoryPropertyReferences);
            });
          }
        });

        // append forecast layer data to primary layer data
        if (combinedForecastLayerDataMap && combinedForecastLayerDataMap.count() > 0) {
          var _layersWithAlignedDat;
          const appendedPrimaryLayerData = primaryLayerData.concat(combinedForecastLayerDataMap.toList());
          const primaryLayerDataCategoryCount = primaryLayerData.map(dataItem => dataItem.get(primaryLayerCategoryField)).toSet().size;
          layersWithAlignedData = layersWithAlignedData.setIn([primaryLayerIndex, 'excludePointsFromTooltip'], // if excludePointsFromTooltip is already configured, it should not be overridden
          (_layersWithAlignedDat = layersWithAlignedData.getIn([primaryLayerIndex, 'excludePointsFromTooltip'])) !== null && _layersWithAlignedDat !== void 0 ? _layersWithAlignedDat : {
            scope: ExcludePointsFromTooltipScopes.range,
            from: primaryLayerDataCategoryCount
          }).setIn([primaryLayerIndex, 'dataset', 'data'], appendedPrimaryLayerData);
        }

        // append forecast layer category property references to primary layer category property references
        if (combinedForecastLayerCategoryPropertyReferences && combinedForecastLayerCategoryPropertyReferences.count() > 0) {
          layersWithAlignedData = layersWithAlignedData.mergeIn([primaryLayerIndex, 'dataset', 'properties', primaryLayerCategoryField, 'references'], combinedForecastLayerCategoryPropertyReferences);
        }

        // merge defined order lists for primary and forecast layers, if applicable
        if (Array.isArray(forecastInsightLayers) && forecastInsightLayers.length) {
          // all forecast layers should have the same defined order, so just take the first one
          const forecastLayerDefinedOrder = forecastInsightLayers[0].layer.getIn([categoryAxis, 'order', 'defined']);
          if (forecastLayerDefinedOrder) {
            const appendedPrimaryLayerDefinedOrder = (primaryLayerDefinedOrder || List()).concat(forecastLayerDefinedOrder);
            layersWithAlignedData = layersWithAlignedData.setIn([primaryLayerIndex, categoryAxis, 'order', 'defined'], appendedPrimaryLayerDefinedOrder);
          }
        }
      }
    }
  });
  return layersWithAlignedData;
};
export const mapVisualization = (xyVisualization, debug = DEFAULT_DEBUG, __gates = [], setFilterOverlapsModalProps = () => {}, reportingThemes) => {
  var _reportingThemes$get;
  const {
    data,
    layers: initialLayers,
    style = Style({}),
    events = Events({}),
    axisTotal
  } = xyVisualization;
  const defaultMarkType = style.get('type') || MarkTypes.bar;
  const orientation = style.get('orient', 'vertical');
  let title = {
    text: null
  };
  const mappedLayers = initialLayers.map((layer, index) => layer.set('id', String(index)).update('dataset', dataset => applyTransformations(data.get(dataset).set('key', dataset), layer.get('transform'))).update('mark', mark => mark || MarkEncoding({})).update('mark', mark => mark.update('type', type => type || defaultMarkType).update('type', type => type && type.toLowerCase()).update('stack', stack => stack || String(index))).update('x', x => x || AxisEncoding({
    type: AxisTypes.hidden
  })).update('y', y => y || AxisEncoding({
    type: AxisTypes.hidden
  })).update('sizing', sizing => sizing || SizingEncoding({})).update('detail', detail => detail || DetailEncoding({})).update('color', color => color || ColorEncoding({})).update('color', color => color.update('defined', defined => defined || ImmutableMap())).updateIn(['x', 'blend'], xAxis => xAxis || String(index)).updateIn(['y', 'blend'], yAxis => yAxis || String(index))).map(layer => setLayerOrdering(layer));
  const hasForecastInsightLayer = mappedLayers.some(isForecastInsightLayer);
  const layers = hasForecastInsightLayer ? alignLayerDataForForecastInsights(mappedLayers) : mappedLayers;

  /* Highcharts categorical y axes is buggy with linear x, this lets us represent it normally in encodings but inverted in highcharts */
  const axes = invertAxes({
    xAxis: mapLayersToAxes(layers, 'x', style),
    yAxis: mapLayersToAxes(layers, 'y', style)
  }, orientation);
  const {
    inverted = false
  } = axes;
  const invertedLayers = inverted ? layers.map(layer => {
    const {
      x,
      y
    } = layer;
    return layer.set('x', y).set('y', x);
  }) : layers;
  const styleColors = style.get('colors') || List();
  const tokenizedColors = (_reportingThemes$get = reportingThemes === null || reportingThemes === void 0 ? void 0 : reportingThemes.get(THEME.MULTI)) !== null && _reportingThemes$get !== void 0 ? _reportingThemes$get : getLegacyDefaultColorOptions('xyMapper');
  const colors = !styleColors.isEmpty() && styleColors.get('colorsTheme') !== THEME.MULTI ? styleColors : List(tokenizedColors);
  const patterns = styleColors.get('patterns') || List();
  const nextColor = makeColorPicker(colors);
  const nextPattern = makePatternPicker(patterns);
  const delta = style.get('delta');
  if (delta) {
    // Display the current data sum and delta value in title if showDelta
    const compareTotal = delta.get('initial');
    const primaryTotal = delta.get('final');
    const direction = primaryTotal > compareTotal ? 'increase' : primaryTotal < compareTotal ? 'decrease' : null;
    const rate = rateOfChange(compareTotal, primaryTotal);
    title = label({
      total: primaryTotal,
      direction,
      rate
    });
  }
  let seriesIndexCounter = 0;
  let series = invertedLayers.map(layer => mapLayerSeries(layer, invertedLayers, events, debug).map(serie => serie.set('index', seriesIndexCounter++).set('xAxis', layer.getIn(['x', 'blend'])).set('yAxis', layer.getIn(['y', 'blend'])).update('style', seriesStyle => seriesStyle || SeriesStyle({})).updateIn(['style', 'color'], color => {
    return !layer.getIn(['linkedTo', 'colorCoordinate']) ? applyOpacity(color || nextColor(), style.get('opacity', 100)) : color;
  }).updateIn(['style', 'pattern'], pattern => pattern || nextPattern()).update(serieUpdate => {
    const type = serieUpdate.get('type');
    if (type === MarkTypes.bar) {
      return serieUpdate.set('type', inverted ? 'bar' : 'column');
    }
    if (type === MarkTypes.tick) {
      return serieUpdate.set('type', MarkTypes.scatter).updateIn(['style', 'marker'], (marker = ImmutableMap()) => marker.set('symbol', MarkTypes.tick).set('lineWidth', marker.get('lineWidth') || 1).set('lineColor', null));
    }
    return serieUpdate;
  })).update(layerSeries => !inverted && layer.getIn(['mark', 'stacking']) ? layerSeries.reverse() : layerSeries)).flatten(1);
  series = setConfiguredLinkedSeries(series, layers);
  series = setSeriesNameForInsightLayers(series, layers);
  series = mapSeriesWithEvents(invertedLayers, events, debug, series, setFilterOverlapsModalProps);
  const options = {
    title,
    type: style.get('type'),
    dataLabels: style.get('dataLabels'),
    stackLabels: style.get('stackLabels'),
    colors: style.get('colors', List([])),
    showLegend: style.get('showLegend') === undefined ? true : style.get('showLegend'),
    showBubbleLegend: style.get('showBubbleLegend'),
    bottomLegend: style.get('bottomLegend'),
    tooltipFormatter: style.get('tooltipFormatter'),
    tooltipDisabled: style.get('isMobile') || style.get('tooltipDisabled'),
    showTotals: style.get('showTotals'),
    legendPosition: style.get('legendPosition'),
    legendWidth: style.get('legendWidth'),
    dataLabelFormat: style.get('dataLabelFormat'),
    disableSeriesHighlighting: style.get('isMobile'),
    dateResolution: style.get('dateResolution'),
    disableZoom: style.get('disableZoom'),
    showZeroesInTooltip: style.get('showZeroesInTooltip'),
    axisTotal
  };
  const chart = Chart({
    axes,
    series,
    options
  });
  debounce(() => debug.set('Chart', chart), 1);
  return buildChartOptions({
    chart,
    layers
  }, reportingThemes);
};
export const __TESTABLE__ = {
  alignLayerDataForForecastInsights
};